import type { ReactNode } from 'react';
import { cx } from '../internal/cx';

export type TrailEventStatus = 'pending' | 'active' | 'done' | 'proof' | 'error';

export interface TrailEvent {
  id: string;
  label: ReactNode;
  /** Data/hora já formatada pelo consumidor — este package não formata data
   * (regra de negócio/i18n fica fora). */
  timestamp?: ReactNode;
  description?: ReactNode;
  status: TrailEventStatus;
}

export interface TrailTimelineProps {
  events: TrailEvent[];
  className?: string;
  'aria-label'?: string;
}

const STATUS_TEXT: Record<TrailEventStatus, string> = {
  pending: 'pendente',
  active: 'em andamento',
  done: 'concluído',
  proof: 'prova verificada',
  error: 'falhou',
};

/**
 * A trilha de acompanhamento — linha (aqui, vertical: é um log cronológico de
 * eventos, mais legível em 375px que uma linha horizontal longa) com nós,
 * sequencial, sem curvas decorativas nem teia (BRAND-EXTRACT §7). `status="proof"`
 * é a ÚNICA condição que pinta o nó de Proof Lime — reservado para o evento que
 * realmente é prova/conclusão/verificação (ex.: "Documento assinado por todos",
 * "Evidências verificadas"). Os demais status nunca usam Lime.
 */
export function TrailTimeline({ events, className, ...rest }: TrailTimelineProps) {
  return (
    <ol
      className={cx('argink-trail-timeline', className)}
      aria-label={rest['aria-label'] ?? 'Linha do tempo'}
    >
      {events.map((event) => (
        <li
          key={event.id}
          className={cx(
            'argink-trail-timeline__event',
            `argink-trail-timeline__event--${event.status}`,
          )}
        >
          <span className="argink-trail-timeline__node" aria-hidden="true" />
          <span className="argink-trail-timeline__body">
            <span className="argink-trail-timeline__label">{event.label}</span>
            <span className="argink-sr-only"> ({STATUS_TEXT[event.status]})</span>
            {event.timestamp ? (
              <span className="argink-trail-timeline__timestamp">{event.timestamp}</span>
            ) : null}
            {event.description ? (
              <span className="argink-trail-timeline__description">{event.description}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
